import { useState, useRef, useEffect } from 'react';
import { BsBriefcase } from 'react-icons/bs';
import { BiSitemap } from 'react-icons/bi';
import { FaRegUser } from 'react-icons/fa';
import { BsCheckSquare, BsListTask, BsCalendar2Week, BsBug } from 'react-icons/bs';
import { IoChevronUpOutline } from 'react-icons/io5';
import { useModal } from '../context/ModalContext';
import { useProjectsStore } from '../state/projects';
import { useParams } from 'react-router';
import * as Sentry from "@sentry/react";

interface CreateDropdownProps {
  onSelectProject: () => void;
  onSelectRoom?: () => void;
  onSelectTeamMember?: () => void;
  onSelectTask?: () => void;
  onSelectActionItem?: () => void;
  onSelectDailyUpdate?: () => void;

}

export const CreateDropdown = ({ onSelectProject, onSelectTeamMember, onSelectTask, onSelectActionItem, onSelectDailyUpdate }: CreateDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const params = useParams<{ projectId: string }>();
  const projectData = useProjectsStore((state) => 
    state.fullProjects.find((p) => p.id === params.projectId)
  );
  const { setShowCreateRoomModal, setCreateRoomModalProps } = useModal();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleReportIssue = () => {
    // Show Sentry feedback dialog for user bug reports
    Sentry.showReportDialog({
      eventId: "user-feedback",
      title: "Report an Issue",
      subtitle: "Help us improve by reporting bugs or issues",
      subtitle2: "Your feedback helps us make the app better",
      labelName: "Name",
      labelEmail: "Email", 
      labelComments: "Describe the issue...",
      labelClose: "Cancel",
      labelSubmit: "Submit Report",
      errorGeneric: "Failed to submit report",
      errorFormEntry: "Please fill in all required fields",
      successMessage: "Thank you for your report!"
    });
    setIsOpen(false);
  };

  const handleCreateRoom = () => {
    // Get siteId from current project
    const siteId = typeof projectData === 'object' && projectData?.sites?.[0]?.site_id || projectData?.sites?.[0]?.id || '';
    
    setCreateRoomModalProps({
      projectId: params.projectId,
      siteId: siteId
    });
    setShowCreateRoomModal(true);
    setIsOpen(false);
  };



  return (
    <>
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="h-10 rounded-[20px] bg-white border border-[rgba(112,115,147,0.16)] text-[#556DF4] pl-8 pr-7 py-[7px] cursor-pointer transition-all hover:bg-[#F0F1F4] flex items-center gap-2 text-[15px] font-['Arcadia_Text',system-ui,sans-serif]"
      >
        Create
        <IoChevronUpOutline className={`text-[rgb(54,54,68)] transition-transform ${isOpen ? '' : 'rotate-180'}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-[20px] border-[0.5px] border-[rgba(112,115,147,0.16)] shadow-[0_2px_8px_rgba(0,0,0,0.05)] py-2" style={{ zIndex: 999999 }}>
          {/* General Section */}
          <button
            onClick={() => {
              onSelectProject();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-gray-700 hover:bg-[#F0F1F4] flex items-center gap-3"
          >
            <BsBriefcase className="text-primary" />
            Project
          </button>
          <button
            onClick={handleCreateRoom}
            className="w-full px-4 py-2 text-left text-gray-700 hover:bg-[#F0F1F4] flex items-center gap-3"
          >
            <BiSitemap className="text-primary" />
            Room
          </button>
          
          {/* Separator - only show if there are project content items */}
          {params.projectId && (onSelectTask || onSelectActionItem || onSelectDailyUpdate) && (
            <div className="mx-4 my-2 border-t border-gray-200"></div>
          )}
          
          {/* Project Content Section */}
          {onSelectTask && params.projectId && (
            <button 
              onClick={() => {
                onSelectTask();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-gray-700 hover:bg-[#F0F1F4] flex items-center gap-3"
            >
              <BsCheckSquare className="text-primary" />
              Task
            </button>
          )}
          {onSelectActionItem && params.projectId && (
            <button 
              onClick={() => {
                onSelectActionItem();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-gray-700 hover:bg-[#F0F1F4] flex items-center gap-3"
            >
              <BsListTask className="text-primary" />
              Action Item
            </button>
          )}
          {onSelectDailyUpdate && params.projectId && (
            <button 
              onClick={() => {
                onSelectDailyUpdate();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-gray-700 hover:bg-[#F0F1F4] flex items-center gap-3"
            >
              <BsCalendar2Week className="text-primary" />
              Daily Update
            </button>
          )}

          
          {/* Separator */}
          {onSelectTeamMember && params.projectId && (
            <div className="mx-4 my-2 border-t border-gray-200"></div>
          )}
          
          {/* Team Section */}
          {onSelectTeamMember && params.projectId && (
            <button 
              onClick={() => {
                onSelectTeamMember();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-gray-700 hover:bg-[#F0F1F4] flex items-center gap-3"
            >
              <FaRegUser className="text-primary" />
              Team Member
            </button>
          )}

          {/* Separator for Support */}
          <div className="mx-4 my-2 border-t border-gray-200"></div>
          
          {/* Support Section */}
          <button 
            onClick={handleReportIssue}
            className="w-full px-4 py-2 text-left text-gray-700 hover:bg-[#F0F1F4] flex items-center gap-3"
          >
            <BsBug className="text-orange-500" />
            Report Issue
          </button>
        </div>
      )}
    </div>


    </>
  );
}; 